import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';

export const Textarea = forwardRef(function Textarea(
  {
    className,
    resizable = true,
    ...props
  }: { className?: string; resizable?: boolean } & Omit<Headless.TextareaProps, 'as' | 'className'>,
  ref: React.ForwardedRef<HTMLTextAreaElement>
) {
  return (
    <span
      data-slot="control"
      className={clsx([
        className,
        // Basic layout
        'tw:relative tw:block tw:w-full',
        // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
        'tw:before:absolute tw:before:inset-px tw:before:rounded-[calc(theme(borderRadius.lg)-1px)] tw:before:bg-white tw:before:shadow',
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        'tw:dark:before:hidden',
        // Focus ring
        'tw:after:pointer-events-none tw:after:absolute tw:after:inset-0 tw:after:rounded-lg tw:after:ring-inset tw:after:ring-transparent tw:sm:after:focus-within:ring-2 tw:sm:after:focus-within:ring-brand-green-0',
        // Disabled state
        'tw:has-[[data-disabled]]:opacity-50 tw:before:has-[[data-disabled]]:bg-zinc-950/5 tw:before:has-[[data-disabled]]:shadow-none',
      ])}
    >
      <Headless.Textarea
        ref={ref}
        {...props}
        className={clsx([
          // Basic layout
          'tw:relative tw:block tw:h-full tw:w-full tw:appearance-none tw:rounded-lg tw:px-[calc(theme(spacing[3.5])-1px)] tw:py-[calc(theme(spacing[2.5])-1px)] tw:sm:px-[calc(theme(spacing.3)-1px)] tw:sm:py-[calc(theme(spacing[1.5])-1px)]',
          // Typography
          'tw:text-base/6 tw:text-zinc-950 tw:placeholder:text-zinc-500 tw:sm:text-sm/6 tw:dark:text-white',
          // Border
          'tw:border tw:border-zinc-950/10 tw:data-[hover]:border-zinc-950/20 tw:dark:border-white/10 tw:dark:data-[hover]:border-white/20',
          // Background color
          'tw:bg-transparent tw:dark:bg-white/5',
          // Hide default focus styles
          'tw:focus:outline-none',
          // Invalid state
          'tw:data-[invalid]:border-red-500 tw:data-[invalid]:data-[hover]:border-red-500 tw:data-[invalid]:dark:border-red-600 tw:data-[invalid]:data-[hover]:dark:border-red-600',
          // Disabled state
          'tw:disabled:border-zinc-950/20 tw:disabled:dark:border-white/15 tw:disabled:dark:bg-white/[2.5%] tw:dark:data-[hover]:disabled:border-white/15',
          // Resizable
          resizable ? 'tw:resize-y' : 'tw:resize-none',
        ])}
      />
    </span>
  );
});
